import { LicenseInfo } from "@mui/x-license";

LicenseInfo.setLicenseKey(
	"97e3c639e0d5298222f79cb9a1fc0cafTz0xMjU1NjcsRT0xODAyMzkwMzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI="
);

import {
	Box,
	Button,
	CircularProgress,
	CssBaseline,
	Stack,
	ThemeProvider,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { fetchFrontendConfig } from "./api";
import { Root } from "./components";
import { setTheme } from "./components/theme";
import { setClientContext, syncSentryAuthState } from "./utils/sentry";

import "./api/fetch";

type FrontendConfigState = {
	isLoading: boolean;
	config: Awaited<ReturnType<typeof fetchFrontendConfig>> | null;
	retryStatus: {
		isRetrying: boolean;
		attempt: number;
		nextDelayMs: number | null;
		lastError: string | null;
	};
};

const AppBootstrap = () => {
	const [state, setState] = useState<FrontendConfigState>({
		isLoading: true,
		config: null,
		retryStatus: {
			isRetrying: false,
			attempt: 0,
			nextDelayMs: null,
			lastError: null,
		},
	});

	useEffect(() => {
		let isCancelled = false;

		const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

		const loadWithRetry = async () => {
			const maxAttempts = 60;
			let delay = 500;
			const maxDelay = 8000;
			const factor = 2;
			const jitterMs = 250;

			let attempt = 0;

			while (!isCancelled) {
				try {
					setState((s) => ({
						...s,
						retryStatus: { ...s.retryStatus, isRetrying: false },
					}));

					const config = await fetchFrontendConfig();

					if (isCancelled) return;

					if (config.sentryDsn) {
						const Sentry = await import("@sentry/react");
						Sentry.init({
							dsn: config.sentryDsn,
							environment: config.environment || undefined,
							release: config.release || undefined,
							integrations: [
								Sentry.browserTracingIntegration(),
								Sentry.replayIntegration({
									maskAllText: false,
									blockAllMedia: false,
								}),
							],
							tracesSampleRate: 0.1,
							replaysOnErrorSampleRate: 1.0, // Record replay when error occurs (buffers last 60s)
						});
						// Expose Sentry globally for test verification
						(window as any).Sentry = Sentry;
					}

					setState({
						isLoading: false,
						config,
						retryStatus: {
							isRetrying: false,
							attempt: 0,
							nextDelayMs: null,
							lastError: null,
						},
					});
					return;
				} catch (error: unknown) {
					attempt += 1;

					if (attempt >= maxAttempts) {
						console.error("[Bootstrap] Max retry attempts reached", error);
						setState((s) => ({
							...s,
							isLoading: false,
							retryStatus: {
								...s.retryStatus,
								isRetrying: false,
								lastError:
									error instanceof Error
										? error.message
										: String(error),
							},
						}));
						return;
					}

					const jitter = Math.floor(Math.random() * jitterMs);
					const waitMs = Math.min(delay, maxDelay) + jitter;

					if (attempt === 1) {
						console.warn(
							"[Bootstrap] Frontend config fetch failed, retrying..."
						);
					}

					if (!isCancelled) {
						setState((s) => ({
							...s,
							retryStatus: {
								isRetrying: true,
								attempt,
								nextDelayMs: waitMs,
								lastError:
									error instanceof Error
										? error.message
										: String(error),
							},
						}));
					}

					await sleep(waitMs);
					delay = Math.min(delay * factor, maxDelay);
					setClientContext();
					syncSentryAuthState();
				}
			}
		};

		void loadWithRetry();

		return () => {
			isCancelled = true;
		};
	}, []);

	if (state.isLoading) {
		return (
			<ThemeProvider theme={setTheme("light")}>
				<CssBaseline />
				<Box
					sx={{
						height: "100vh",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
					}}
					data-component="bootstrap-loading"
				>
					<CircularProgress />
					<Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
						<Typography variant="body2" color="text.secondary">
							{state.retryStatus.isRetrying
								? `Connecting to backend… retry ${state.retryStatus.attempt}` +
									(state.retryStatus.nextDelayMs
										? `, next attempt in ${Math.ceil(state.retryStatus.nextDelayMs / 1000)}s`
										: "")
								: "Loading configuration…"}
						</Typography>
						{state.retryStatus.lastError ? (
							<Typography
								variant="caption"
								color="text.disabled"
								sx={{ textAlign: "center", px: 2 }}
							>
								Last error: {state.retryStatus.lastError}
							</Typography>
						) : null}
						{state.retryStatus.isRetrying ? (
							<Button
								size="small"
								variant="outlined"
								onClick={() => window.location.reload()}
								data-component="reload-button"
							>
								Reload now
							</Button>
						) : null}
					</Stack>
				</Box>
			</ThemeProvider>
		);
	}

	if (!state.config) {
		return (
			<ThemeProvider theme={setTheme("light")}>
				<CssBaseline />
				<Box
					sx={{
						height: "100vh",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						px: 2,
					}}
					data-component="bootstrap-error"
				>
					<Typography variant="h6" color="error" sx={{ mb: 2 }}>
						Failed to load configuration
					</Typography>
					{state.retryStatus.lastError ? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 2, textAlign: "center" }}
						>
							{state.retryStatus.lastError}
						</Typography>
					) : null}
					<Button
						variant="contained"
						onClick={() => window.location.reload()}
						data-component="reload-button"
					>
						Reload page
					</Button>
				</Box>
			</ThemeProvider>
		);
	}

	return <Root />;
};

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(<AppBootstrap />);
